import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';

import { ChannelDetailsModal } from './ChannelDetailsModal';
import {
  getChannelIcon,
  getChannelTypeLabel,
} from './ChannelDisplayComponents';
import {
  extractHostname,
  formatContactValue,
  formatFeeAmount,
  formatServicesProvided,
  getEstimatedFee,
} from './formatters';

interface ChannelTableProps {
  channels: ReadDepartmentRecordsChannel[];
  onEdit: (channel: ReadDepartmentRecordsChannel) => void;
  onDelete: (channelId: string) => void;
}

export const ChannelTable = ({
  channels,
  onEdit,
  onDelete,
}: ChannelTableProps) => {
  const [detailsChannel, setDetailsChannel] =
    useState<ReadDepartmentRecordsChannel | null>(null);

  return (
    <>
      <div className="overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Type
              </th>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Contact
              </th>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Name
              </th>
              <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Services
              </th>
              <th className="px-3 py-1.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                Fee
              </th>
              <th className="w-12 px-2 py-1.5">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {channels.map((channel) => {
              const Icon = getChannelIcon(channel);
              const typeLabel = getChannelTypeLabel(channel);
              const contactValue = formatContactValue(channel);
              const estimatedFee = getEstimatedFee(channel);
              const servicesFormatted = formatServicesProvided(
                channel.services_provided,
              );
              const hasDetails =
                channel.instructions ||
                channel.mailing_instructions ||
                channel.required_document_url;

              const isWebsite =
                channel.handle?.handle_type?.toUpperCase() === 'WEBSITE';
              const websiteUrl = isWebsite ? channel.handle?.value : null;

              return (
                <tr key={channel.id} className="group hover:bg-gray-50/50">
                  {/* Type Column */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                      <span className="text-xs text-gray-700">{typeLabel}</span>
                    </div>
                  </td>

                  {/* Contact Column */}
                  <td className="px-3 py-2">
                    {isWebsite && websiteUrl ? (
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        title={websiteUrl}
                      >
                        {extractHostname(websiteUrl)}
                      </a>
                    ) : (
                      <span
                        className="line-clamp-1 text-xs text-gray-600"
                        title={contactValue || undefined}
                      >
                        {contactValue || '—'}
                      </span>
                    )}
                  </td>

                  {/* Name Column */}
                  <td className="px-3 py-2">
                    <span className="text-xs text-gray-900">
                      {channel.handle?.name || '—'}
                    </span>
                  </td>

                  {/* Services Column */}
                  <td className="px-3 py-2">
                    {servicesFormatted ? (
                      <span
                        className="text-xs text-gray-600"
                        title={servicesFormatted.full}
                      >
                        {servicesFormatted.display}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  {/* Fee Column */}
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs text-gray-900">
                      {estimatedFee !== null
                        ? formatFeeAmount(estimatedFee)
                        : '—'}
                    </span>
                  </td>

                  {/* Actions Dropdown */}
                  <td className="px-2 py-2">
                    <Dropdown>
                      <DropdownButton
                        plain
                        className="opacity-0 group-hover:opacity-100"
                        aria-label="Actions"
                      >
                        <EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        {hasDetails && (
                          <DropdownItem
                            onClick={() => setDetailsChannel(channel)}
                          >
                            <EyeIcon />
                            View Details
                          </DropdownItem>
                        )}
                        <DropdownItem onClick={() => onEdit(channel)}>
                          <PencilIcon />
                          Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => onDelete(channel.id)}>
                          <TrashIcon />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailsChannel && (
        <ChannelDetailsModal
          channel={detailsChannel}
          onClose={() => setDetailsChannel(null)}
        />
      )}
    </>
  );
};
